import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import LongHeadlineStep from './Steps/LongHeadlineStep'
import StepForm from '../../../../../features/components/Forms/StepForm'
import NameStep from '../../ReusableSteps/NameStep'
import LinkStep from '../../ReusableSteps/LinkStep'
import HeadlinesStep from '../../ReusableSteps/HeadlinesStep'
import DescriptionsStep from '../../ReusableSteps/DescriptionsStep'
import CallToActionStep from '../../ReusableSteps/CallToActionStep'
import BusinessNameStep from '../../ReusableSteps/BusinessNameStep.js'
import LandscapeImageStep from '../../ReusableSteps/LandscapeImageStep'
import SquareImageStep from '../../ReusableSteps/SquareImageStep'
import SquareLogoStep from '../../ReusableSteps/SquareLogoStep'
import LandscapeLogoStep from '../../ReusableSteps/LandscapeLogoStep'
import StatusStep from '../../ReusableSteps/StatusStep'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import useIsInternalAdCreation from '../../../../../features/hooks/internalAdHooks/useIsInternalAdCreation'

import { clearCreateAd, createAd } from '../../../../../modules/actions/ads'
import {
  selectedPartnershipRightsHolderIdSelector,
  selectedPartnershipSelector
} from '../../../../../modules/selectors/partnerships'
import { selectedGoogleAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import {
  adWasCreatedSelector,
  adCreateErrorSelector,
  adCreateLoadingSelector
} from '../../../../../modules/selectors/ads'
import { lineItemsLoadingSelector } from '../../../../../modules/selectors/lineItems'

import { getInitialValues } from './fields'
import { validationSchema } from './validations'
import { transformValuesToBE } from './formatters'

import { AD_GOOGLE_CREATE } from '../../../../../constants/forms'
import { GOOGLE_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { STATUS } from '../../fields'
import { IS_QUARANTINE } from '../../../../reusableFields'

const AdGoogleCreateForm = ({ formProgressItems, onSuccessSubmit, lineItemId, campaignId }) => {
  const dispatch = useDispatch()

  const adWasCreated = useSelector(adWasCreatedSelector)
  const isLineItemLoading = useSelector(lineItemsLoadingSelector)
  const rightsHolderId = useSelector(selectedPartnershipRightsHolderIdSelector)
  const { partner_name: partnerName } = useSelector(selectedPartnershipSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  const isInternalAdCreation = useIsInternalAdCreation()

  const onSubmit = useCallback(
    values => {
      const transformedValues = transformValuesToBE(values)
      const adData = {
        ad: transformedValues,
        account: selectedAdAccountId,
        ad_group_id: lineItemId,
        campaign_id: campaignId,
        status: values[STATUS],
        // isInternalAdCreation is default, also allow passing it manually if user has ability to select it(values[IS_QUARANTINE])
        ...((isInternalAdCreation || values[IS_QUARANTINE]) && {
          quarantine: true,
          account_id: rightsHolderId,
          rights_holder_account_id: rightsHolderId
        })
      }

      dispatch(createAd(adData, GOOGLE_PLATFORM))
    },
    [dispatch, selectedAdAccountId, lineItemId, campaignId, isInternalAdCreation, rightsHolderId]
  )

  const initialValues = useMemo(
    () => getInitialValues(partnerName, isInternalAdCreation),
    [isInternalAdCreation, partnerName]
  )
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const steps = useMemo(
    () => [
      {
        component: <BusinessNameStep />,
        show: true
      },
      {
        component: <LinkStep />,
        show: true
      },
      {
        component: <HeadlinesStep showPreview={false} />,
        show: true
      },
      {
        component: <DescriptionsStep showPreview={false} />,
        show: true
      },
      {
        component: <LongHeadlineStep />,
        show: true
      },
      // marketing_images
      {
        component: <LandscapeImageStep />,
        show: true
      },
      // square_marketing_images
      {
        component: <SquareImageStep />,
        show: true
      },
      // square_logo_images
      {
        component: <SquareLogoStep />,
        show: true
      },
      // logo_images
      {
        component: <LandscapeLogoStep />,
        show: true
      },
      {
        component: <CallToActionStep />,
        show: true
      },
      {
        component: <NameStep />,
        show: true
      },
      {
        component: <StatusStep />,
        show: true
      }
    ],
    []
  )

  const clearSubmitHandler = useCallback(() => {
    dispatch(clearCreateAd())
  }, [dispatch])

  return (
    <StepForm
      steps={steps}
      formik={purifiedFormik}
      formName={AD_GOOGLE_CREATE}
      formProgressItems={formProgressItems}
      // processing
      isLoadingSelector={adCreateLoadingSelector}
      errorSelector={adCreateErrorSelector}
      successSubmit={adWasCreated}
      isInitialDataLoading={isLineItemLoading}
      // after form submit
      onSuccessSubmit={onSuccessSubmit}
      clearSubmitHandler={clearSubmitHandler}
    />
  )
}

AdGoogleCreateForm.propTypes = {
  formProgressItems: PropTypes.array,
  onSuccessSubmit: PropTypes.func,
  lineItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}
export default AdGoogleCreateForm
